import React, { useState } from 'react';
import {
  CheckCircleIcon,
  ClockIcon,
  UserIcon,
  LightBulbIcon,
  FlagIcon,
  ArrowRightIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline';
import { CheckCircleIcon as CheckCircleSolidIcon } from '@heroicons/react/24/solid';

export default function ActionPlan({ insights }) {
  const [completedActions, setCompletedActions] = useState(new Set());

  if (!insights) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <p className="text-gray-500 text-center">Loading action plan...</p>
      </div>
    );
  }

  const toggleAction = (index) => {
    const newCompleted = new Set(completedActions);
    if (newCompleted.has(index)) {
      newCompleted.delete(index);
    } else {
      newCompleted.add(index);
    }
    setCompletedActions(newCompleted);
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'critical':
        return 'bg-red-100 text-red-800 border-red-300';
      case 'high':
        return 'bg-orange-100 text-orange-800 border-orange-300';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'low':
        return 'bg-green-100 text-green-800 border-green-300';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-300';
    }
  };

  return (
    <div className="space-y-6">
      {/* Next Actions */}
      {insights.next_actions && insights.next_actions.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <CheckCircleIcon className="w-6 h-6 text-blue-600" />
              Next Actions
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Complete these actions to move your project forward
            </p>
          </div>
          <div className="divide-y divide-gray-200">
            {insights.next_actions.map((action, idx) => (
              <div
                key={idx}
                className={`p-6 transition-all ${
                  completedActions.has(idx) ? 'bg-gray-50 opacity-60' : 'hover:bg-gray-50'
                }`}
              >
                <div className="flex items-start gap-4">
                  <button
                    onClick={() => toggleAction(idx)}
                    className="mt-1 flex-shrink-0"
                  >
                    {completedActions.has(idx) ? (
                      <CheckCircleSolidIcon className="w-6 h-6 text-green-600" />
                    ) : (
                      <div className="w-6 h-6 border-2 border-gray-300 rounded-full hover:border-blue-500 transition-colors" />
                    )}
                  </button>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3
                        className={`text-lg font-semibold ${
                          completedActions.has(idx)
                            ? 'text-gray-500 line-through'
                            : 'text-gray-900'
                        }`}
                      >
                        {action.action}
                      </h3>
                      <span
                        className={`px-3 py-1 rounded-full text-xs font-medium border whitespace-nowrap ${getPriorityColor(
                          action.priority
                        )}`}
                      >
                        {action.priority?.toUpperCase()}
                      </span>
                    </div>

                    <div className="space-y-3">
                      <div className="flex items-start gap-2 text-sm text-gray-700">
                        <LightBulbIcon className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                        <div>
                          <span className="font-medium">Why:</span> {action.why}
                        </div>
                      </div>

                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <UserIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                          <div>
                            <span className="font-medium">Who:</span> {action.who}
                          </div>
                        </div>

                        <div className="flex items-center gap-2 text-sm text-gray-700">
                          <ClockIcon className="w-4 h-4 text-purple-600 flex-shrink-0" />
                          <div>
                            <span className="font-medium">When:</span> {action.when}
                          </div>
                        </div>
                      </div>

                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                        <p className="text-sm text-blue-900">
                          <span className="font-medium">How:</span> {action.how}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Milestones */}
      {insights.milestones && insights.milestones.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FlagIcon className="w-6 h-6 text-purple-600" />
              Suggested Milestones
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Key checkpoints to track project progress
            </p>
          </div>
          <div className="p-6">
            <div className="space-y-6">
              {insights.milestones.map((milestone, idx) => (
                <div key={idx} className="relative">
                  {idx < insights.milestones.length - 1 && (
                    <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-purple-200" />
                  )}
                  <div className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center border-2 border-purple-300">
                        <span className="text-sm font-bold text-purple-700">{idx + 1}</span>
                      </div>
                    </div>
                    <div className="flex-1 pb-6">
                      <div className="flex items-start justify-between mb-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          {milestone.name}
                        </h3>
                        <span className="text-sm text-purple-600 font-medium whitespace-nowrap ml-4">
                          {milestone.target_date}
                        </span>
                      </div>
                      
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm font-medium text-gray-700 mb-1">Deliverables:</p>
                          <ul className="list-disc list-inside text-sm text-gray-600 space-y-1">
                            {milestone.deliverables?.map((deliverable, dIdx) => (
                              <li key={dIdx}>{deliverable}</li>
                            ))}
                          </ul>
                        </div>
                        
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <p className="text-sm text-green-900">
                            <span className="font-medium">Success:</span> {milestone.success_criteria}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Critical Path */}
      {insights.critical_path && insights.critical_path.length > 0 && (
        <div className="bg-gradient-to-r from-orange-50 to-red-50 rounded-lg shadow border border-orange-200">
          <div className="p-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
              <ArrowRightIcon className="w-6 h-6 text-orange-600" />
              Critical Path
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              These activities must happen in sequence - delays here impact the entire project
            </p>
            <div className="flex flex-wrap items-center gap-2">
              {insights.critical_path.map((step, idx) => (
                <React.Fragment key={idx}>
                  <div className="bg-white px-4 py-2 rounded-lg border border-orange-300 shadow-sm">
                    <p className="text-sm font-medium text-gray-900">{step}</p>
                  </div>
                  {idx < insights.critical_path.length - 1 && (
                    <ArrowRightIcon className="w-5 h-5 text-orange-600 flex-shrink-0" />
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Risks */}
      {insights.risks && insights.risks.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <ExclamationTriangleIcon className="w-6 h-6 text-red-600" />
              Risk Alerts
            </h2>
          </div>
          <div className="p-6">
            <ul className="space-y-3">
              {insights.risks.map((risk, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <span className="text-gray-700">{risk}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Recommendations */}
      {insights.recommendations && insights.recommendations.length > 0 && (
        <div className="bg-white rounded-lg shadow">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <LightBulbIcon className="w-6 h-6 text-yellow-600" />
              Strategic Recommendations
            </h2>
          </div>
          <div className="p-6">
            <ul className="space-y-3">
              {insights.recommendations.map((rec, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <div className="w-6 h-6 bg-yellow-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                    <span className="text-xs font-bold text-yellow-700">{idx + 1}</span>
                  </div>
                  <span className="text-gray-700">{rec}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {/* Metadata */}
      <div className="bg-gray-50 rounded-lg p-4 text-sm text-gray-600">
        <div className="flex items-center justify-between">
          <span>
            Generated by: <span className="font-medium">{insights.source || 'AI'}</span>
          </span>
          <span>
            Confidence: <span className="font-medium">{((insights.confidence_score || 0) * 100).toFixed(0)}%</span>
          </span>
        </div>
      </div>
    </div>
  );
}
